import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { defineMessages, useIntl } from 'react-intl';

import env from 'Env';
import Meta from 'Components/Utility/SEO/Meta';
import { formatDrmName, formatPlatformsName } from 'Helpers/Product/FormatMetaNames';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import { history } from 'Redux/store';
import isPrerender from 'Helpers/SEO/is-prerender';
import { MetaTag } from 'Helpers/SEO/Tags';

function ProductSEOHelmet({ product, liveMystery }) {
  const intl = useIntl();
  if (!product) return null;

  const { formatMessage } = intl;

  const messages = defineMessages({
    steamgame: {
      id: 'product.steamgame',
      defaultMessage: 'Steam Game',
    },
    download: {
      id: 'product.download',
      defaultMessage: 'Download',
    },
    game: {
      id: 'product.game.game',
      defaultMessage: 'Game',
    },
    preorder: {
      id: 'product.preorder.preorder',
      defaultMessage: 'Pre-order {gameName}',
    },
    dlc: {
      id: 'product.dlc',
      defaultMessage: 'Downloadable Content',
    },
    software: {
      id: 'product.software',
      defaultMessage: 'Software',
    },
    audio: {
      id: 'product.audio',
      defaultMessage: 'Audio',
    },
    video: {
      id: 'product.video',
      defaultMessage: 'Video',
    },
    elearning: {
      id: 'product.elearning',
      defaultMessage: 'eLearning',
    },
    elearningBundle: {
      id: 'product-seo.elearning-bundle',
      defaultMessage: 'eLearning Bundle',
    },
    eBookBundle: {
      id: 'product-seo.eBook-bundle',
      defaultMessage: 'eBook Bundle',
    },
    softwareBundle: {
      id: 'product-seo.software-bundle',
      defaultMessage: 'Software Bundle',
    },
    audioBundle: {
      id: 'product-seo.audio-bundle',
      defaultMessage: 'Audio Bundle',
    },
    gameBundle: {
      id: 'product-seo.game-bundle',
      defaultMessage: 'Game Bundle',
    },
    steamGameBundle: {
      id: 'product-seo.steam-game-bundle',
      defaultMessage: 'Steam Game Bundle',
    },
    savePercentage: {
      id: 'product-seo.save-percentage',
      defaultMessage: 'Save {percentageDiscount}',
    },
    buy: {
      id: 'product-seo.buy',
      defaultMessage: 'Buy {gameName}',
    },
  });

  const seoProductTitle = () => {
    const name = product.name;

    const platforms = formatPlatformsName(product.platforms, '').replace('Windows', 'PC');

    const drm = formatDrmName(product.drm, '');

    const brand = 'Fanatical';

    let type = '';

    if (product.display_type === 'software') {
      type = formatMessage(messages.software);
    } else if (product.display_type === 'elearning') {
      type = formatMessage(messages.elearning);
    } else if (drm && !drm.includes('Games') && product.type === 'game') {
      type = formatMessage(messages.game);
    } else if (product.type === 'dlc') {
      type = formatMessage(messages.dlc);
    }

    const preorderName = product.preorder && !product.presale ? `${formatMessage(messages.preorder, { gameName: name })}` : name;

    const defaultTitle = `${preorderName} | ${platforms} ${drm} ${type} | ${brand}`;

    const savingString = formatMessage(messages.savePercentage, { percentageDiscount: `${Math.round(get(product, 'current_discount.percent', 0) * 100)}%` });
    const highlightSavingTitle = `${savingString} - ${name} | ${platforms} ${drm} ${type}`;

    if (env.seoTitleSavingProducts.includes(product.slug) && get(product, 'current_discount.percent')) {
      return highlightSavingTitle;
    }

    const variantGroup = get(product, 'seo.titleSplitGroup', 0);

    if (variantGroup === 1 || variantGroup === 2) {
      return defaultTitle;
    }

    if (variantGroup >= 3) {
      // Combination of variant 1 and 3 (groups 3 and 5)
      return `${name} | ${drm} ${platforms.includes('PC') ? 'PC' : ''} ${type}`;
    }

    return defaultTitle;
  };

  const seoBundleTitle = () => {
    let bundleDrm = formatMessage(messages.gameBundle);

    product.bundles.forEach((tier) => {
      tier.games.forEach((game) => {
        if (game.drm && game.drm.steam === true) {
          bundleDrm = formatMessage(messages.steamGameBundle);
        }
      });
    });

    if (['book-bundle', 'comic-bundle'].includes(product.display_type)) {
      bundleDrm = formatMessage(messages.eBookBundle);
    }

    if (product.display_type === 'elearning-bundle') {
      bundleDrm = formatMessage(messages.elearningBundle);
    }

    if (product.display_type === 'software-bundle') {
      bundleDrm = formatMessage(messages.softwareBundle);
    }

    return `${product.name} | ${bundleDrm} | Fanatical`;
  };

  const title = product.type === 'bundle' ? seoBundleTitle() : seoProductTitle();

  let mysteryRedirect = false;

  if (liveMystery && product.type === 'bundle' && product.mystery && product.sold_out && !window.location.pathname.includes('preview')) {
    mysteryRedirect = `https://${env.appUrl}/${window.locale || 'en'}/${liveMystery.type}/${liveMystery.slug}`;
    if (!isPrerender()) {
      history.replace(`/${liveMystery.type}/${liveMystery.slug}`);
      return null;
    }
  }

  const videoUrl = `${env.videoCdnUrl}${product.hitcardVideo}`;

  let hideFromPrerender = false;
  if (product.type === 'bundle' && !!getUrlParameter('product') && product.availability && product.availability.valid_until) {
    const expiredTime = new Date(product.availability.valid_until).getTime();
    if (expiredTime < Date.now()) {
      hideFromPrerender = true;
    }
  }

  return (
    <React.Fragment>
      <Meta
        title={title}
        description={product.seo.desc || product.desc}
        url={`${product.type}/${product.slug}`}
        image={`product/1280x720/${product.cover}`}
        pageType="ItemPage"
        pageName={product.name}
        datePublished={product.release}
        dateModified={product.last_modified}
      />
      {mysteryRedirect &&
        <MetaTag name="prerender-status-code" content="302" />
      }
      {mysteryRedirect &&
        <MetaTag name="prerender-header" content={`Location: ${mysteryRedirect}`} />
      }
      {hideFromPrerender &&
        <MetaTag name="prerender-status-code" content="404" />
      }
      {product.hide_from_search &&
        <MetaTag name="robots" content="noindex" />
      }
      {!!product.hitcardVideo &&
        <MetaTag name="og:video" content={videoUrl} />
      }
      {!!product.hitcardVideo &&
        <MetaTag name="og:video:url" content={videoUrl} />
      }
      {!!product.hitcardVideo &&
        <MetaTag name="og:video:secure_url" content={videoUrl} />
      }
      {!!product.hitcardVideo &&
        <MetaTag name="og:video:type" content="video/mp4" />
      }
      {!!product.hitcardVideo &&
        <MetaTag name="og:video:width" content="853" />
      }
      {!!product.hitcardVideo &&
        <MetaTag name="og:video:type" content="480" />
      }
    </React.Fragment>
  );
}

ProductSEOHelmet.propTypes = {
  product: PropTypes.shape({}),
  liveMystery: PropTypes.shape({}),
};

ProductSEOHelmet.defaultProps = {
  product: {},
  liveMystery: null,
};

const mapStateToProps = ({
  mystery,
}) => ({
  liveMystery: mystery.all.find(product => product.type === 'bundle' && product.mystery && !product.mystery_upsell),
});

export default connect(mapStateToProps)(ProductSEOHelmet);
